/**
 * MAR Review Queue — Phase 2.B Step 7 (Frontend Minimal)
 *
 * Tenant-wide page that lets QA / Consultants / PMs walk MAR documents
 * through their approval workflow.
 *
 * Backend:
 *   GET  /api/pece/mar                  (with optional status / project_id)
 *   GET  /api/pece/mar/pending          (in-flight states only)
 *   GET  /api/pece/mar/{id}
 *   POST /api/pece/mar/{id}/submit
 *   POST /api/pece/mar/{id}/qa-approve
 *   POST /api/pece/mar/{id}/consultant-approve
 *   POST /api/pece/mar/{id}/reject
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  ClipboardCheck, RefreshCw, Loader2, ListChecks, Activity, FileText,
  Check, X, AlertTriangle, ChevronDown, ChevronUp, FileBarChart, ShieldCheck,
} from 'lucide-react';

const STATUS_META = {
  draft:              { label: 'مسودة',              cls: 'bg-gray-100 text-gray-700 border-gray-300' },
  submitted:          { label: 'مُقدّم',              cls: 'bg-blue-50 text-blue-700 border-blue-300' },
  qa_review:          { label: 'تحت مراجعة الجودة',   cls: 'bg-indigo-50 text-indigo-700 border-indigo-300' },
  consultant_review:  { label: 'تحت مراجعة الاستشاري', cls: 'bg-violet-50 text-violet-700 border-violet-300' },
  approved:           { label: 'مُعتمد',              cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  rejected:           { label: 'مرفوض',               cls: 'bg-rose-50 text-rose-700 border-rose-300' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const MARReviewQueue = () => {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const path = tab === 'pending' ? '/pece/mar/pending' : '/pece/mar?limit=300';
        const r = await axiosInstance.get(path);
        if (!cancelled) setRows(r.data);
      } catch (e) {
        if (!cancelled) toast.error('فشل تحميل قائمة MAR');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [tab, reloadTick]);

  const reload = () => setReloadTick((t) => t + 1);

  const act = async (mar, endpoint, successMsg) => {
    setActingOn(mar.id);
    try {
      const r = await axiosInstance.post(`/pece/mar/${mar.id}/${endpoint}`, {});
      toast.success(`${successMsg} · ${r.data.mar_no}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الإجراء');
    } finally {
      setActingOn(null);
    }
  };

  const submit  = (m) => act(m, 'submit',                'تم التقديم');
  const qaApprove = (m) => act(m, 'qa-approve',          'تم اعتماد الجودة');
  const consultantApprove = (m) => act(m, 'consultant-approve', 'تم اعتماد الاستشاري — تمت المصادقة');

  const reject = async (mar, reason) => {
    setActingOn(mar.id);
    try {
      await axiosInstance.post(`/pece/mar/${mar.id}/reject`, { reason });
      toast.success(`تم رفض ${mar.mar_no}`);
      setRejectModal(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الرفض');
    } finally {
      setActingOn(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mar-review-page">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs text-violet-600 font-bold uppercase tracking-wider mb-1 flex items-center gap-2">
                <ClipboardCheck className="w-4 h-4" /> MAR · Material Approval Requests
              </div>
              <h1 className="text-2xl font-bold text-gray-900">قائمة مراجعة طلبات اعتماد المواد</h1>
              <p className="text-sm text-gray-500 mt-1">
                يستعرض ويعتمد طلبات اعتماد المواد التي تم توليدها تلقائياً من طلبات الشراء المعتمدة.
              </p>
            </div>
            <button
              data-testid="mar-refresh-btn"
              onClick={reload}
              className="px-4 py-2.5 border border-violet-300 text-violet-700 rounded-lg hover:bg-violet-50 flex items-center gap-2 font-semibold text-sm"
            >
              <RefreshCw className="w-4 h-4" /> تحديث
            </button>
          </div>

          <div className="flex gap-1 mt-5">
            <TabBtn id="pending" active={tab} onClick={setTab} icon={<ListChecks className="w-4 h-4" />}>
              قيد المراجعة <span className="ml-2 bg-violet-100 text-violet-700 text-xs px-2 py-0.5 rounded-full">{tab === 'pending' ? rows.length : '·'}</span>
            </TabBtn>
            <TabBtn id="all" active={tab} onClick={setTab} icon={<Activity className="w-4 h-4" />}>
              كل MARs
            </TabBtn>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {loading && (
          <div className="flex items-center justify-center py-20 text-gray-500" data-testid="mar-loading">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> جاري التحميل...
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center" data-testid="mar-empty">
            <FileText className="w-12 h-12 mx-auto text-violet-500 mb-3" />
            <h3 className="text-lg font-bold text-gray-800">لا توجد طلبات اعتماد مواد</h3>
            <p className="text-sm text-gray-500 mt-1">
              {tab === 'pending'
                ? 'لا توجد طلبات قيد المراجعة حالياً. سيتم توليدها تلقائياً عند اعتماد طلبات الشراء (PPR).'
                : 'لم يتم إنشاء أي MAR بعد.'}
            </p>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="space-y-3" data-testid="mar-list">
            {rows.map((m) => (
              <MARCard
                key={m.id}
                mar={m}
                expanded={expandedId === m.id}
                isActing={actingOn === m.id}
                onToggle={() => setExpandedId(expandedId === m.id ? null : m.id)}
                onSubmit={() => submit(m)}
                onQaApprove={() => qaApprove(m)}
                onConsultantApprove={() => consultantApprove(m)}
                onReject={() => setRejectModal(m)}
              />
            ))}
          </div>
        )}
      </div>

      {rejectModal && (
        <RejectModal
          mar={rejectModal}
          onCancel={() => setRejectModal(null)}
          onConfirm={(reason) => reject(rejectModal, reason)}
          isActing={actingOn === rejectModal.id}
        />
      )}
    </div>
  );
};

const TabBtn = ({ id, active, onClick, children, icon }) => (
  <button
    data-testid={`mar-tab-${id}`}
    onClick={() => onClick(id)}
    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
      active === id
        ? 'border-violet-600 text-violet-700'
        : 'border-transparent text-gray-600 hover:text-gray-900'
    }`}
  >
    {icon}{children}
  </button>
);

const MARCard = ({ mar, expanded, isActing, onToggle,
                   onSubmit, onQaApprove, onConsultantApprove, onReject }) => {
  const meta = STATUS_META[mar.status] || STATUS_META.draft;
  const canSubmit  = mar.status === 'draft';
  const canQa      = mar.status === 'submitted';
  const canConsult = mar.status === 'qa_review';
  const canReject  = ['submitted', 'qa_review', 'consultant_review'].includes(mar.status);

  return (
    <div
      data-testid={`mar-card-${mar.id}`}
      className="bg-white rounded-xl border border-gray-200 hover:shadow-sm transition"
    >
      <div className="p-5">
        <div className="flex items-start gap-4 flex-wrap">
          <div className={`px-3 py-2 rounded-lg border ${meta.cls} text-center min-w-[140px]`}>
            <ShieldCheck className="w-5 h-5 mx-auto" />
            <div className="text-[10px] uppercase font-bold tracking-wider mt-1">{meta.label}</div>
          </div>

          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-mono font-bold text-base text-gray-900">{mar.mar_no}</div>
                {mar.ppr_no && (
                  <div className="text-xs text-gray-500">
                    Linked PPR: <span className="font-mono font-semibold text-gray-700">{mar.ppr_no}</span>
                  </div>
                )}
                {mar.activity_code && (
                  <div className="text-xs text-gray-600">
                    Activity: <span className="font-mono">{mar.activity_code}</span>
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase text-gray-500 font-semibold">Source</div>
                <div className="text-xs font-semibold text-violet-700">
                  {mar.source === 'manual' ? 'يدوي' : 'تلقائي'}
                </div>
              </div>
            </div>

            {/* Materials summary */}
            {Array.isArray(mar.materials) && mar.materials.length > 0 && (
              <div className="mt-3 p-3 bg-violet-50 border border-violet-100 rounded-lg flex items-center gap-3 text-sm">
                <FileBarChart className="w-4 h-4 text-violet-600" />
                <div className="flex-1">
                  <span className="font-semibold">{mar.materials[0].material_name || '—'}</span>
                  <span className="text-xs text-violet-700 ml-2">
                    · مطلوب: {mar.materials[0].requested_qty || 0}
                    · محجوز: {mar.materials[0].reserved_qty || 0}
                  </span>
                </div>
                {mar.materials.length > 1 && (
                  <span className="text-xs text-violet-700">+{mar.materials.length - 1} عناصر</span>
                )}
              </div>
            )}

            {/* Rejection */}
            {mar.status === 'rejected' && mar.rejection_reason && (
              <div className="mt-2 p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-700 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
                <div><strong>سبب الرفض:</strong> {mar.rejection_reason}</div>
              </div>
            )}

            <div className="mt-3 text-[10px] text-gray-400">
              {fmtDate(mar.created_at)}
              {mar.correlation_id && (
                <a
                  href={`/ecp?correlation_id=${mar.correlation_id}`}
                  className="ml-3 text-violet-700 underline hover:no-underline"
                  data-testid={`mar-ecp-link-${mar.id}`}
                >
                  عرض السلسلة في ECP →
                </a>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 min-w-[140px]">
            {canSubmit && (
              <button
                data-testid={`mar-submit-${mar.id}`}
                disabled={isActing}
                onClick={onSubmit}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-1"
              >
                {isActing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                تقديم
              </button>
            )}
            {canQa && (
              <button
                data-testid={`mar-qa-approve-${mar.id}`}
                disabled={isActing}
                onClick={onQaApprove}
                className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-1"
              >
                {isActing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                اعتماد الجودة
              </button>
            )}
            {canConsult && (
              <button
                data-testid={`mar-consultant-approve-${mar.id}`}
                disabled={isActing}
                onClick={onConsultantApprove}
                className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-1"
              >
                {isActing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                اعتماد الاستشاري
              </button>
            )}
            {canReject && (
              <button
                data-testid={`mar-reject-${mar.id}`}
                disabled={isActing}
                onClick={onReject}
                className="px-3 py-2 bg-white border border-rose-300 text-rose-700 rounded-lg text-sm font-semibold hover:bg-rose-50 disabled:opacity-50 flex items-center justify-center gap-1"
              >
                <X className="w-4 h-4" /> رفض
              </button>
            )}
            <button
              data-testid={`mar-toggle-${mar.id}`}
              onClick={onToggle}
              className="px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs hover:bg-gray-50 flex items-center justify-center gap-1"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              التفاصيل
            </button>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-100 text-xs grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`mar-details-${mar.id}`}>
            <DetailBlock title="QA Review" block={mar.qa_review} />
            <DetailBlock title="Consultant Review" block={mar.consultant_review} />
            <Field label="Project ID" value={mar.project_id} mono />
            <Field label="Reservation ID" value={mar.reservation_id || '—'} mono />
            <Field label="Submitted At" value={fmtDate(mar.submitted_at)} />
            <Field label="Approved At" value={fmtDate(mar.approved_at)} />
            {mar.notes && <Field label="Notes" value={mar.notes} />}
          </div>
        )}
      </div>
    </div>
  );
};

const DetailBlock = ({ title, block }) => (
  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
    <div className="text-[10px] uppercase font-bold text-gray-500 mb-1">{title}</div>
    {block ? (
      <div className="space-y-0.5">
        <div>المراجع: <span className="font-semibold">{block.reviewer_name || '—'}</span></div>
        <div>التاريخ: {fmtDate(block.reviewed_at)}</div>
        {block.notes && <div>ملاحظات: {block.notes}</div>}
      </div>
    ) : (
      <div className="text-gray-400 italic">لم يتم بعد</div>
    )}
  </div>
);

const Field = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] uppercase font-bold text-gray-500">{label}</div>
    <div className={mono ? 'font-mono text-gray-800' : 'text-gray-800'}>{value}</div>
  </div>
);

const RejectModal = ({ mar, onCancel, onConfirm, isActing }) => {
  const [reason, setReason] = useState('');
  return (
    <div
      data-testid="mar-reject-modal"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold mb-1">رفض MAR</h3>
        <p className="text-sm text-gray-600 mb-4">
          سيتم رفض <span className="font-mono font-bold">{mar.mar_no}</span>. السبب مطلوب.
        </p>
        <textarea
          data-testid="mar-reject-reason"
          placeholder="السبب (إجباري)..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4 h-24 resize-none"
        />
        <div className="flex justify-end gap-2">
          <button
            data-testid="mar-reject-cancel"
            onClick={onCancel}
            disabled={isActing}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
          >
            إلغاء
          </button>
          <button
            data-testid="mar-reject-confirm"
            onClick={() => reason.trim() && onConfirm(reason.trim())}
            disabled={isActing || !reason.trim()}
            className="px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-semibold hover:bg-rose-700 disabled:opacity-50 flex items-center gap-1"
          >
            {isActing ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
            تأكيد الرفض
          </button>
        </div>
      </div>
    </div>
  );
};

export default MARReviewQueue;
